import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@core/services/locale.service.js';
import { LoggerService } from '@core/services/logger.service.js';
import { environment } from '@environments/environment';
import { AuthService, type LoggedUserDto } from '@mmscan/miportal-angular-client';
import type { Locale } from '@mmscan/shared-types';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter, firstValueFrom } from 'rxjs';

//TODO: reset permissions after x minutes - reset when refreshing token
type AuthState = {
  loggedUser: LoggedUserDto | null;
  loggedUserLoading: boolean;
  loggedUserError: string | null;

  /**
   * Per-resource permissions loaded lazily by route guards and components.
   * Keys are resource names, values are the allowed actions array.
   */
  featuresByResource: Record<string, string[]>;

  /** Resources currently being fetched — prevents duplicate requests */
  resourcesLoading: Set<string>;

  userLocale: Locale;

  isAuthenticated: boolean;
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(() => {
    const localeService = inject(LocaleService);
    return {
      loggedUser: null,
      loggedUserLoading: false,
      loggedUserError: null,
      featuresByResource: {},
      resourcesLoading: new Set<string>(),
      userLocale: localeService.getCurrentLocale(),
      isAuthenticated: false,
    };
  }),
  withComputed((state) => ({
    isLoggedIn: computed(() => state.isAuthenticated() && state.loggedUser() !== null),
    userId: computed(() => state.loggedUser()?.id),
  })),
  withMethods((store) => {
    const authService = inject(AuthService);
    const oidcSecurityService = inject(OidcSecurityService);
    const localeService = inject(LocaleService);
    const logger = inject(LoggerService);
    const router = inject(Router);

    return {
      /**
       * Clears all auth state and sends the user to /sign-in. Invoked when
       * we've confirmed the session is truly lost (e.g. refresh token was
       * rejected, or a recovery refresh after SilentRenewFailed also failed).
       */
      handleSessionLoss(): void {
        patchState(store, {
          loggedUser: null,
          featuresByResource: {},
          resourcesLoading: new Set<string>(),
          isAuthenticated: false,
        });
        router.navigate(['/sign-in']);
      },

      async loadLoggedUserAsync(): Promise<void> {
        if (store.loggedUserLoading()) return;

        patchState(store, { loggedUserLoading: true, loggedUserError: null });

        try {
          const response = await firstValueFrom(authService.getLoggedUser());
          logger.debug('loading loggedUser response', response);
          patchState(store, {
            loggedUser: response.data,
            loggedUserLoading: false,
            loggedUserError: null,
          });
        } catch (error) {
          logger.error('Failed to load logged user:', error);
          patchState(store, {
            loggedUserError: 'Failed to load user info. Please try again.',
            loggedUserLoading: false,
          });
        }
      },

      // ── Per-resource features (loaded lazily by route guards) ──

      /**
       * Returns true if features for the given resource are already cached
       * (either from navFeatures or from a previous lazy load).
       */
      isResourceLoaded(resource: string): boolean {
        return resource in store.featuresByResource();
      },

      isResourceLoading(resource: string): boolean {
        return store.resourcesLoading().has(resource);
      },

      /**
       * Get the cached actions for a resource (from either source).
       * Returns undefined if not yet loaded.
       */
      getResourceActions(resource: string): string[] | undefined {
        return store.featuresByResource()[resource];
      },

      /**
       * Load features for multiple resources in a single batched request.
       * Skips resources already cached or currently loading.
       * Prefer this over calling loadFeaturesForResource in a loop.
       */
      async loadFeaturesByResources(resources: readonly string[]): Promise<void> {
        const pending = resources.filter((r) => !this.isResourceLoaded(r) && !store.resourcesLoading().has(r));

        if (pending.length === 0) return;

        // Mark all pending as loading
        const loading = new Set(store.resourcesLoading());
        for (const r of pending) loading.add(r);
        patchState(store, { resourcesLoading: loading });

        try {
          const response = await firstValueFrom(authService.getLoggedUserFeatures(pending));
          logger.debug(`loaded features for resources [${pending.join(', ')}]`, response);

          const updated = { ...store.featuresByResource() };
          for (const r of pending) {
            updated[r] = response.data[r] ?? [];
          }

          const doneLoading = new Set(store.resourcesLoading());
          for (const r of pending) doneLoading.delete(r);

          patchState(store, {
            featuresByResource: updated,
            resourcesLoading: doneLoading,
          });
        } catch (error) {
          logger.error(`Failed to load features for resources [${pending.join(', ')}]:`, error);

          // Cache empty arrays so we don't retry endlessly
          const updated = { ...store.featuresByResource() };
          for (const r of pending) updated[r] = [];

          const doneLoading = new Set(store.resourcesLoading());
          for (const r of pending) doneLoading.delete(r);

          patchState(store, {
            featuresByResource: updated,
            resourcesLoading: doneLoading,
          });
        }
      },

      /**
       * Load features for a single resource from the backend.
       * No-ops if the resource is already cached or currently loading.
       */
      async loadFeaturesForResource(resource: string): Promise<void> {
        // Already have it
        if (this.isResourceLoaded(resource)) return;

        // Already fetching
        if (store.resourcesLoading().has(resource)) return;

        // Mark as loading
        const loading = new Set(store.resourcesLoading());
        loading.add(resource);
        patchState(store, { resourcesLoading: loading });

        try {
          const response = await firstValueFrom(authService.getLoggedUserFeature(resource));
          logger.debug(`loaded features for resource "${resource}"`, response);

          const updated = { ...store.featuresByResource() };
          // The response may contain the resource key or may be empty (no permissions)
          updated[resource] = response.data[resource] ?? [];

          const doneLoading = new Set(store.resourcesLoading());
          doneLoading.delete(resource);

          patchState(store, {
            featuresByResource: updated,
            resourcesLoading: doneLoading,
          });
        } catch (error) {
          logger.error(`Failed to load features for resource "${resource}":`, error);

          // Remove from loading set but cache an empty array so we don't retry endlessly
          const updated = { ...store.featuresByResource() };
          updated[resource] = [];

          const doneLoading = new Set(store.resourcesLoading());
          doneLoading.delete(resource);

          patchState(store, {
            featuresByResource: updated,
            resourcesLoading: doneLoading,
          });
        }
      },

      async checkAuth() {
        // Skip if already authenticated and user data is loaded
        if (store.isAuthenticated() && store.loggedUser() !== null) {
          return;
        }

        const response = await firstValueFrom(oidcSecurityService.checkAuth());

        if (response.isAuthenticated) {
          // Only track auth status here — the access token is intentionally NOT
          // stored in this signal store. It is always read live from
          // OidcSecurityService so that silent token renewal is transparent.
          patchState(store, { isAuthenticated: true });

          await this.loadLoggedUserAsync();
        }
      },

      login(): void {
        patchState(store, { loggedUserLoading: true });
        oidcSecurityService.authorize();
      },

      logout(): void {
        logger.debug('Logout called');

        oidcSecurityService.logoffLocal();

        patchState(store, {
          loggedUser: null,
          featuresByResource: {},
          resourcesLoading: new Set<string>(),
          isAuthenticated: false,
        });

        const logoutUrl = `${environment.cognitoDomain}/logout?client_id=${environment.cognitoClientId}&redirect_uri=${encodeURIComponent(environment.url)}&response_type=code`;
        logger.debug('Logout URL:', logoutUrl);
        window.location.href = logoutUrl;
      },

      switchLocale(locale?: Locale): void {
        const currentLocale = store.userLocale();
        const newLocale = locale ?? localeService.getAlternateLocale(currentLocale);

        if (newLocale === currentLocale) return;

        const applyLocale = () => {
          localeService.switchLocale(newLocale);
          patchState(store, { userLocale: newLocale });
        };

        // Update user preference in backend
        if (store.loggedUser()?.id) {
          authService
            .updateLoggedUser({
              locale: newLocale,
            })
            .subscribe({
              next: applyLocale,
              error: (error) => {
                logger.error('Failed to update user locale:', error);
                // Still switch locale locally even if backend update fails
                applyLocale();
              },
            });
        } else {
          // If no logged user, just switch locale locally
          applyLocale();
        }
      },
    };
  }),
  withHooks((store) => {
    const oidcSecurityService = inject(OidcSecurityService);
    const publicEventsService = inject(PublicEventsService);
    const logger = inject(LoggerService);

    return {
      onInit() {
        // Log every OIDC library event so the full renewal lifecycle is visible
        // in the console (debug builds only — LogLevel.Error in production).
        publicEventsService.registerForEvents().subscribe((event) => {
          const label = EventTypes[event.type] ?? `EventType(${event.type})`;
          switch (event.type) {
            case EventTypes.SilentRenewStarted:
              logger.debug(`[AuthStore] OIDC ▶ ${label}`);
              break;
            case EventTypes.NewAuthenticationResult:
              logger.debug(`[AuthStore] OIDC ✔ ${label} — new token received`);
              break;
            case EventTypes.SilentRenewFailed:
              logger.warn(`[AuthStore] OIDC ✖ ${label}`, event.value);
              break;
            case EventTypes.IdTokenExpired:
              logger.warn(`[AuthStore] OIDC ⚠ ${label}`);
              break;
            default:
              logger.debug(`[AuthStore] OIDC ${label}`, event.value);
          }
        });

        /**
         * When the OIDC library fires SilentRenewFailed, don't immediately
         * boot the user out — a single failed renewal can be caused by a
         * transient network blip, a brief Cognito 5xx, or a Wi-Fi handoff.
         *
         * Instead, attempt one explicit forceRefreshSession() call. If that
         * also fails (meaning the refresh token is actually rejected or we
         * stayed offline), THEN clear state and redirect to /sign-in.
         *
         * This prevents the "user logged out for no reason" UX that happens
         * when a single scheduled renewal races a flaky network.
         *
         * The `recoveryInProgress` flag serialises concurrent recoveries.
         * Without it, if SilentRenewFailed fires twice in rapid succession
         * (e.g. a network flap), two forceRefreshSession() calls race each
         * other. With Cognito refresh-token rotation enabled the second call
         * arrives with an already-rotated (invalidated) token, gets rejected,
         * and triggers handleSessionLoss() — logging the user out even though
         * the first call already restored the session.
         */
        let recoveryInProgress = false;

        publicEventsService
          .registerForEvents()
          .pipe(filter((event) => event.type === EventTypes.SilentRenewFailed))
          .subscribe(() => {
            if (recoveryInProgress) {
              logger.warn('[AuthStore] Silent renew failed again — recovery already in progress, ignoring');
              return;
            }

            recoveryInProgress = true;
            logger.warn('[AuthStore] Silent renew failed — attempting one manual recovery refresh');

            firstValueFrom(oidcSecurityService.forceRefreshSession())
              .then((result) => {
                recoveryInProgress = false;

                if (result?.isAuthenticated && result.accessToken) {
                  logger.debug('[AuthStore] Recovery refresh succeeded, session restored');
                  patchState(store, { isAuthenticated: true });
                  return;
                }

                logger.warn('[AuthStore] Recovery refresh returned unauthenticated — signing out');
                store.handleSessionLoss();
              })
              .catch((err: unknown) => {
                recoveryInProgress = false;
                logger.error('[AuthStore] Recovery refresh threw — signing out', err);
                store.handleSessionLoss();
              });
          });

        // ── Forced renewal stress-test (local dev only) ────────────────────
        // Set tokenRenewalTestIntervalSeconds in environment.local.ts to drive
        // a forceRefreshSession() call on that cadence. Lets you verify the
        // full renewal path without waiting for the Cognito token to expire.
        if (environment.tokenRenewalTestIntervalSeconds) {
          const intervalMs = environment.tokenRenewalTestIntervalSeconds * 1000;
          logger.warn(
            `[AuthStore] ⚠ Token renewal stress-test ACTIVE — forcing refresh every ${environment.tokenRenewalTestIntervalSeconds}s`
          );
          setInterval(() => {
            logger.debug('[AuthStore] ⏱ Stress-test: calling forceRefreshSession()…');
            firstValueFrom(oidcSecurityService.forceRefreshSession())
              .then((result) => {
                if (result?.isAuthenticated && result.accessToken) {
                  logger.debug('[AuthStore] ✔ Stress-test renewal succeeded', {
                    tokenLength: result.accessToken.length,
                  });
                } else {
                  logger.warn('[AuthStore] ✖ Stress-test renewal returned unauthenticated', result);
                }
              })
              .catch((err: unknown) => {
                logger.error('[AuthStore] ✖ Stress-test renewal threw', err);
              });
          }, intervalMs);
        }
      },
    };
  })
);
